import { SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { App, Button, Form, Input, InputNumber, Slider } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { MainContent } from '../../components/MainContent';
import type { AiSettingsInput } from '../../graphql/generated/graphql';
import { AI_SETTINGS_QUERY, UPDATE_AI_SETTINGS_MUTATION } from './AiSettings.gql';

export const AiSettings: React.FC = () => {
  const [form] = useForm<AiSettingsInput>();
  const [updateMutation, { loading: updateLoading }] = useMutation(UPDATE_AI_SETTINGS_MUTATION);
  const { message } = App.useApp();

  const save = async (values: AiSettingsInput) => {
    try {
      await updateMutation({ variables: { input: values } });
      message.success('KI-Konfiguration wurde aktualisiert');
    } catch {
      message.error('KI-Konfiguration konnte nicht aktualisiert werden');
    }
  };

  const { data, loading } = useQuery(AI_SETTINGS_QUERY);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <MainContent size="medium">
      <PageHeader title="KI-Konfiguration" style={{ padding: 0, paddingBottom: 'inherit' }} />
      <Form
        labelCol={{ xs: 6, xl: 5 }}
        colon={false}
        labelAlign="left"
        form={form}
        onFinish={save}
        initialValues={{
          baseSystemPrompt: data?.aiSettings?.baseSystemPrompt ?? '',
          temperature: data?.aiSettings?.temperature ?? 0.3,
          maxTokens: data?.aiSettings?.maxTokens ?? 4096,
        }}
      >
        <Form.Item
          name="baseSystemPrompt"
          label="System-Prompt"
          tooltip="Globaler System-Prompt für den KI-Assistenten. Definiert Rolle, Regeln und Antwortformat."
          rules={[{ required: true, whitespace: true }]}
        >
          <Input.TextArea rows={10} />
        </Form.Item>
        <Form.Item
          name="temperature"
          label="Temperature"
          tooltip="0.0 = deterministisch, 1.0 = kreativ. Empfohlen: 0.3"
          rules={[{ type: 'number', min: 0, max: 1 }]}
        >
          <Slider min={0} max={1} step={0.1} marks={{ 0: '0', 0.3: '0.3', 0.5: '0.5', 1: '1' }} style={{ maxWidth: 300 }} />
        </Form.Item>
        <Form.Item
          name="maxTokens"
          label="Max Tokens"
          tooltip="Maximale Anzahl Output-Tokens pro Antwort"
          rules={[{ type: 'number', min: 1 }]}
        >
          <InputNumber min={1} step={512} style={{ width: 120 }} />
        </Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          disabled={updateLoading || form.getFieldsError().some(({ errors }) => errors.length)}
          icon={<SaveOutlined />}
        >
          Speichern
        </Button>
      </Form>
    </MainContent>
  );
};
